import React, { useState, useEffect } from 'react';
import { DEPARTMENTS, PROCESS_PATHS, fetchWithFallback } from '../referenceData';

const SAFETY_FIELDS = {
  1: ['Incidents', 'Near Misses', 'Trailer Audits', 'Dwelling ISAs > 8 Days Completed', 'Dwelling ISAs > 8 Days', 'IPC: Vendor Defects'],
  2: ['Incidents', 'Near Misses', 'Trailer Audits', 'Loaded Cages', 'Dragonflys assigned to me'],
  3: ['Incidents', 'Near Misses', 'CSX needed DPMO', 'Oversized DPMO', 'True Problem Solve DPMO', 'Reinject cages at EOS', 'Empty Cages at EOS', 'Dragonflys assigned to me'],
  4: ['Incidents', 'Near Misses', 'Trailer Audits', 'Dwelling ISAs > 8 Days Completed', 'Dwelling ISAs > 8 Days', 'IPC: Vendor Defects'],
  5: ['Incidents', 'Near Misses', 'Trailer Audits', 'Loaded Cages', 'RTD OB loads', 'Dragonflys assigned to me'],
  6: ['Incidents', 'Near Misses', 'Trailer Audits', 'Dwelling ISAs > 8 Days Completed', 'Dwelling ISAs > 8 Days', 'Empty Pallet Trailers (Standard)', 'Empty Pallet Trailers (XL)', 'IPC: Vendor Defects', 'Dragonflys assigned to me', 'Pallet Audits'],
  7: ['Incidents', 'Near Misses', 'Stacks of empty pallets staged', 'Working PIT', 'Empty Pallet Trailers (Standard)', 'Empty Pallet Trailers (XL)', 'Dragonflys assigned to me', 'PIT SIMBAS'],
};

export default function DeptStaffing({ api, site }) {
  const [plans, setPlans] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [processPaths, setProcessPaths] = useState([]);
  const [date, setDate] = useState((() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })());
  const [shiftType, setShiftType] = useState('day');
  const [selectedDept, setSelectedDept] = useState('');
  const [fclmData, setFclmData] = useState(null);
  const [safety, setSafety] = useState({});
  const [manualInputs, setManualInputs] = useState({});
  const [handoffNotes, setHandoffNotes] = useState('');
  const [success, setSuccess] = useState('');
  const [recommendations, setRecommendations] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState('');

  useEffect(() => {
    fetchWithFallback(`${api}/api/shift-plans`, []).then(setPlans);
    fetchWithFallback(`${api}/api/departments`, DEPARTMENTS).then(setDepartments);
    fetchWithFallback(`${api}/api/process-paths`, PROCESS_PATHS).then(setProcessPaths);
  }, [api]);

  // Auto-select plan when date/shift changes
  useEffect(() => {
    const match = plans.find(p => p.date === date && p.shift_type === shiftType);
    if (match) setSelectedPlan(String(match.id));
    else setSelectedPlan('');
  }, [date, shiftType, plans]);

  // Fetch FCLM data
  useEffect(() => {
    if (selectedPlan) {
      fetch(`${api}/api/fclm/hourly?site=${site}&date=${date}&shift_type=${shiftType}`)
        .then(r => r.json()).then(setFclmData).catch(() => setFclmData(null));
    }
  }, [selectedPlan, api, site, date, shiftType]);

  // Fetch recommendations when dept changes
  useEffect(() => {
    if (selectedPlan && selectedDept) {
      fetch(`${api}/api/shift-plans/${selectedPlan}/dept/${selectedDept}/recommended`)
        .then(r => r.json()).then(data => {
          if (data && data.recommendations) setRecommendations(data);
          else setRecommendations(null);
        }).catch(() => setRecommendations(null));
    } else { setRecommendations(null); }
  }, [selectedPlan, selectedDept, api]);

  // Reset safety/manual when dept changes, load saved staffing
  useEffect(() => {
    setSafety({});
    setManualInputs({});
    setHandoffNotes('');
    // Load saved planned HCs for this plan+dept
    if (selectedPlan && selectedDept) {
      fetch(`${api}/api/shift-plans/${selectedPlan}`)
        .then(r => r.json()).then(planData => {
          if (planData && planData.staffing) {
            const existing = planData.staffing.find(s => s.department_id === parseInt(selectedDept));
            if (existing && existing.entries) {
              const loaded = {};
              existing.entries.forEach(e => { loaded[`hc_${e.process_path_id}`] = String(e.planned_headcount); });
              setManualInputs(loaded);
            }
          }
        }).catch(() => {});
    }
  }, [selectedDept]);

  const deptPaths = processPaths.filter(p => p.department_id === parseInt(selectedDept));
  const deptName = departments.find(d => d.id === parseInt(selectedDept))?.name || '';

  // Map FCLM labor data to process paths
  const getFclmHours = (pathName) => {
    if (!fclmData?.labor) return 0;
    const row = fclmData.labor.find(r => r.process_path === pathName);
    return row ? row.total : 0;
  };

  const getFclmCplh = (pathName, deptId) => {
    const hrs = getFclmHours(pathName);
    if (!hrs || !fclmData?.cartons) return 0;
    const dept = departments.find(d => d.id === deptId);
    if (!dept) return 0;
    const cartonKey = dept.volume_source === 'pid' ? 'pid' : dept.volume_source === 'aros' ? 'aros' : 'pr';
    const cartons = fclmData.cartons[cartonKey]?.total || 0;
    return cartons / hrs;
  };

  const submitWash = async () => {
    const functions = deptPaths.map(pp => ({
      process_path_id: pp.id,
      name: pp.name,
      hours: getFclmHours(pp.name),
      lp_cplh: pp.baseline_cplh,
      actual_cplh: getFclmCplh(pp.name, pp.department_id),
      manual_hours: manualInputs[pp.id]?.hours,
    }));
    await fetch(`${api}/api/dept-wash`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        site, date, shift_type: shiftType,
        department_id: parseInt(selectedDept),
        safety,
        functions,
        handoff_notes: handoffNotes,
        tot: 0,
      })
    });
    setSuccess(`${deptName} wash submitted!`);
    setTimeout(() => setSuccess(''), 3000);
  };

  const fmtCplh = (v) => !v ? '—' : v >= 100 ? Math.round(v) : v.toFixed(1);
  const safetyFields = SAFETY_FIELDS[parseInt(selectedDept)] || ['Incidents', 'Near Misses'];

  return (
    <div>
      <h2 className="section-title">Department Wash</h2>
      {success && <div className="success-msg">{success}</div>}
      <div className="card">
        <div className="form-row">
          <div className="form-group">
            <label>Date</label>
            <input type="date" value={date} onChange={e => setDate(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Shift Type</label>
            <select value={shiftType} onChange={e => setShiftType(e.target.value)}>
              <option value="day">Day</option>
              <option value="night">Night</option>
            </select>
          </div>
          <div className="form-group">
            <label>Department</label>
            <select value={selectedDept} onChange={e => setSelectedDept(e.target.value)}>
              <option value="">Choose...</option>
              {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
        </div>
        {!selectedPlan && date && <p style={{ fontSize: '0.8rem', color: '#c62828' }}>No shift plan for this date — create one in Main Shift Plan first</p>}
      </div>

      {selectedDept && selectedPlan && (
        <>
          {/* Shift Plan - Planned HC per function */}
          <div className="card">
            <h3 className="section-title">Shift Plan — Planned HC</h3>
            {(() => {
              const dept = departments.find(d => d.id === parseInt(selectedDept));
              const plan = plans.find(p => p.id === parseInt(selectedPlan) || String(p.id) === selectedPlan);
              const getPlannedVolume = () => {
                if (!plan || !plan.volumes || !dept) return 0;
                const getVol = (k, idx) => { const v = plan.volumes.find(v => v.key === k); return v ? v.volume : (plan.volumes[idx]?.volume || 0); };
                if (dept.volume_source === 'pid') return getVol('pid', 0) * dept.volume_multiplier;
                if (dept.volume_source === 'aros') return getVol('aros', 1) * dept.volume_multiplier;
                if (dept.volume_source === 'pallet') return getVol('pallet', 2) * dept.volume_multiplier;
                if (dept.volume_source === 'pid_25_plus_aros') return getVol('pid', 0) * 0.25 + getVol('aros', 1);
                return 0;
              };
              const plannedVolume = getPlannedVolume();

              const acceptLpHc = () => {
                const updates = {};
                deptPaths.forEach(pp => {
                  const lpHc = pp.baseline_cplh > 0 ? (plannedVolume / pp.baseline_cplh / 10) : 0;
                  updates[`hc_${pp.id}`] = lpHc > 0 ? lpHc.toFixed(1) : '';
                });
                setManualInputs({ ...manualInputs, ...updates });
              };

              return (
                <>
                  <p style={{ fontSize: '0.85rem', marginBottom: 8 }}>Planned Volume: <strong>{Math.round(plannedVolume).toLocaleString()}</strong></p>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', padding: '6px 8px' }}>Function</th>
                        <th style={{ padding: '6px 8px' }}>LP CPLH</th>
                        <th style={{ padding: '6px 8px' }}>LP HC</th>
                        <th style={{ padding: '6px 8px', background: '#fff9c4' }}>Planned HC</th>
                        <th style={{ padding: '6px 8px' }}>Trending CPLH</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deptPaths.map(pp => {
                        const lpHc = pp.baseline_cplh > 0 ? (plannedVolume / pp.baseline_cplh / 10) : 0;
                        const plannedHc = parseFloat(manualInputs[`hc_${pp.id}`]) || 0;
                        const trendingCplh = plannedHc > 0 ? (plannedVolume / plannedHc / 10) : 0;
                        return (
                          <tr key={pp.id}>
                            <td style={{ padding: '4px 8px' }}>{pp.name}</td>
                            <td style={{ textAlign: 'center', padding: '4px 8px' }}>{fmtCplh(pp.baseline_cplh)}</td>
                            <td style={{ textAlign: 'center', padding: '4px 8px' }}>{lpHc > 0 ? lpHc.toFixed(1) : '—'}</td>
                            <td style={{ textAlign: 'center', padding: '4px 8px', background: '#fff9c4' }}>
                              <input type="number" min="0" step="0.5" value={manualInputs[`hc_${pp.id}`] || ''}
                                onChange={e => setManualInputs({ ...manualInputs, [`hc_${pp.id}`]: e.target.value })}
                                style={{ width: 60, textAlign: 'center', border: '1px solid #fdd835', borderRadius: 3, padding: '2px 4px' }} />
                            </td>
                            <td style={{ textAlign: 'center', padding: '4px 8px' }}>{trendingCplh > 0 ? fmtCplh(trendingCplh) : '—'}</td>
                          </tr>
                        );
                      })}
                      <tr style={{ fontWeight: 700, borderTop: '2px solid #232f3e' }}>
                        <td style={{ padding: '4px 8px' }}>Total</td>
                        <td></td>
                        <td style={{ textAlign: 'center', padding: '4px 8px' }}>{deptPaths.reduce((s, pp) => s + (pp.baseline_cplh > 0 ? plannedVolume / pp.baseline_cplh / 10 : 0), 0).toFixed(1)}</td>
                        <td style={{ textAlign: 'center', padding: '4px 8px', background: '#fff9c4' }}>
                          {deptPaths.reduce((s, pp) => s + (parseFloat(manualInputs[`hc_${pp.id}`]) || 0), 0).toFixed(1)}
                        </td>
                        <td></td>
                      </tr>
                    </tbody>
                  </table>
                  <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
                    <button className="btn btn-secondary" onClick={acceptLpHc}>Accept LP HC</button>
                    <button className="btn btn-primary" onClick={async () => {
                      const entries = deptPaths.map(pp => ({ process_path_id: pp.id, name: pp.name, planned_headcount: parseFloat(manualInputs[`hc_${pp.id}`]) || 0 }));
                      await fetch(`${api}/api/staffing`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ shift_plan_id: parseInt(selectedPlan), department_id: parseInt(selectedDept), entries }) });
                      setSuccess('Planned HC submitted!'); setTimeout(() => setSuccess(''), 3000);
                    }}>Submit Planned HC</button>
                  </div>
                </>
              );
            })()}
          </div>

          {/* Safety / Standard Work */}
          <div className="card">
            <h3 className="section-title">Safety / Standard Work</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr><th style={{ textAlign: 'left', padding: '6px 8px' }}>Metric</th><th style={{ padding: '6px 8px' }}>Goal</th><th style={{ padding: '6px 8px', background: '#fff9c4' }}>Actual</th><th style={{ padding: '6px 8px' }}>Delta</th></tr>
              </thead>
              <tbody>
                {safetyFields.map(field => (
                  <tr key={field}>
                    <td style={{ padding: '4px 8px' }}>{field}</td>
                    <td style={{ textAlign: 'center', padding: '4px 8px' }}>{field === 'Incidents' || field === 'Near Misses' ? 0 : ''}</td>
                    <td style={{ textAlign: 'center', padding: '4px 8px', background: '#fff9c4' }}>
                      <input type="number" min="0" value={safety[field] || ''} onChange={e => setSafety({ ...safety, [field]: e.target.value })}
                        style={{ width: 60, textAlign: 'center', border: '1px solid #fdd835', borderRadius: 3, padding: '2px 4px' }} />
                    </td>
                    <td style={{ textAlign: 'center', padding: '4px 8px', color: (safety[field] || 0) > 0 ? '#c62828' : '#2e7d32' }}>
                      {safety[field] ? -safety[field] : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Cost/Volume Execution */}
          <div className="card">
            <h3 className="section-title">Cost/Volume Execution</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '6px 8px' }}>Metric</th>
                  <th style={{ padding: '6px 8px' }}>Hours</th>
                  <th style={{ padding: '6px 8px' }}>LP CPLH</th>
                  <th style={{ padding: '6px 8px' }}>Actual CPLH</th>
                  <th style={{ padding: '6px 8px' }}>LP HC</th>
                  <th style={{ padding: '6px 8px' }}>CPLH % to LP</th>
                </tr>
              </thead>
              <tbody>
                {/* Dept total row */}
                <tr style={{ fontWeight: 700, borderBottom: '2px solid #232f3e' }}>
                  <td style={{ padding: '4px 8px' }}>{deptName}</td>
                  <td style={{ textAlign: 'center', padding: '4px 8px' }}>{recommendations ? deptPaths.reduce((s, pp) => s + getFclmHours(pp.name), 0).toFixed(1) : '—'}</td>
                  <td style={{ textAlign: 'center', padding: '4px 8px' }}>{recommendations ? fmtCplh(recommendations.dept_cplh) : '—'}</td>
                  <td style={{ textAlign: 'center', padding: '4px 8px' }}>{fclmData ? fmtCplh(deptPaths.reduce((s, pp) => s + getFclmHours(pp.name), 0) > 0 ? recommendations?.volume / deptPaths.reduce((s, pp) => s + getFclmHours(pp.name), 0) : 0) : '—'}</td>
                  <td style={{ textAlign: 'center', padding: '4px 8px' }}>{recommendations ? recommendations.recommendations.reduce((s, r) => s + r.recommended_hc, 0).toFixed(1) : '—'}</td>
                  <td style={{ textAlign: 'center', padding: '4px 8px' }}></td>
                </tr>
                {/* Function rows */}
                {deptPaths.map(pp => {
                  const hrs = getFclmHours(pp.name);
                  const rec = recommendations?.recommendations?.find(r => r.process_path_id === pp.id);
                  const actualCplh = hrs > 0 && recommendations ? recommendations.volume / hrs : 0;
                  const lpCplh = pp.baseline_cplh;
                  const pctToLp = lpCplh && actualCplh ? Math.round((actualCplh / lpCplh) * 100) : null;
                  return (
                    <tr key={pp.id}>
                      <td style={{ padding: '4px 8px', paddingLeft: 24 }}>{pp.name}</td>
                      <td style={{ textAlign: 'center', padding: '4px 8px' }}>{hrs ? hrs.toFixed(1) : '—'}</td>
                      <td style={{ textAlign: 'center', padding: '4px 8px' }}>{fmtCplh(lpCplh)}</td>
                      <td style={{ textAlign: 'center', padding: '4px 8px' }}>{hrs ? fmtCplh(actualCplh) : '—'}</td>
                      <td style={{ textAlign: 'center', padding: '4px 8px' }}>{rec ? rec.recommended_hc : '—'}</td>
                      <td style={{ textAlign: 'center', padding: '4px 8px', color: pctToLp && pctToLp >= 100 ? '#2e7d32' : '#c62828' }}>{pctToLp ? `${pctToLp}%` : '—'}</td>
                    </tr>
                  );
                })}
                {/* Support roles - only for IB PID Dept (show actual PPR hours) */}
                {parseInt(selectedDept) === 1 && (
                  <>
                    <tr>
                      <td style={{ padding: '4px 8px', paddingLeft: 24 }}>IB PS</td>
                      <td style={{ textAlign: 'center', padding: '4px 8px' }}>{fclmData?.ppr_hours?.fcSummary ? Object.values(fclmData.ppr_hours.fcSummary).reduce((s,v) => s+v, 0).toFixed(1) : '—'}</td>
                      <td style={{ textAlign: 'center', padding: '4px 8px' }}>NA</td>
                      <td colSpan={3}></td>
                    </tr>
                    <tr>
                      <td style={{ padding: '4px 8px', paddingLeft: 24 }}>IB PA</td>
                      <td style={{ textAlign: 'center', padding: '4px 8px' }}>{fclmData?.ppr_hours?.fcSummary ? (Object.values(fclmData.ppr_hours.fcSummary).reduce((s,v) => s+v, 0) * 0.1).toFixed(1) : '—'}</td>
                      <td style={{ textAlign: 'center', padding: '4px 8px' }}>NA</td>
                      <td colSpan={3}></td>
                    </tr>
                  </>
                )}
                <tr style={{ borderTop: '1px solid #ccc' }}>
                  <td style={{ padding: '4px 8px', fontWeight: 600 }}>TOT</td>
                  <td style={{ textAlign: 'center', padding: '4px 8px', background: '#fff9c4' }}>
                    <input type="number" min="0" value={manualInputs.tot || ''} onChange={e => setManualInputs({ ...manualInputs, tot: e.target.value })}
                      style={{ width: 50, textAlign: 'center', border: '1px solid #fdd835', borderRadius: 3, padding: '2px' }} />
                  </td>
                  <td colSpan={4}></td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Handoff Notes */}
          <div className="card">
            <h3 className="section-title">Handoff Notes</h3>
            <textarea value={handoffNotes} onChange={e => setHandoffNotes(e.target.value)}
              placeholder="Enter handoff notes for next shift..."
              style={{ width: '100%', minHeight: 80, padding: 8, borderRadius: 4, border: '1px solid #ccc', fontFamily: 'inherit' }} />
          </div>

          <button className="btn btn-primary" onClick={submitWash} style={{ marginTop: 12 }}>Submit {deptName} Wash</button>
        </>
      )}
    </div>
  );
}
